import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import CTButton from "./CTButton";
import { useUser } from "../contexts/UserContext";
import CTMatchList from "./CTMatchList";

export function CTEdit({
  ref=null, //recibe el ref externo
  label,
  name,
  control,
  type = "text",
  readOnly = false,
  disabled = false,
  visible = true,
  labelPosition = "top",
  caseMode = "normal",
  isEditable = true,
  rules,
  required = false,
  length = null,
  labelMinWidth = 128,
  bold = false,
  highlight = false,
  decimals = null,
  showSpinner = false,
  defaultValue = "",
  onChange: onChangeProp,
  value: propValue,
  matchName = null,
  onMatchClick = null,
  matchPosition = "inside",
  placeholder = "",
  borderBottom = true,
  sufix = "", // sufijo de unidades
  width = null, // nueva prop: longitud en caracteres
  iconIndex = -1,
}) {
  const { matchCodes, matchCodeData, loadMatchCodeData } = useUser();

  const [isEditing, setIsEditing] = useState(isEditable);
  const [localValue, setLocalValue] = useState(defaultValue);
  const [showMatchList, setShowMatchList] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const matchListRef = useRef(null);
  const [matchCode, setMatchCode] = useState(null);
  // --- dentro del componente CTEdit ---
  const [capsLock, setCapsLock] = useState(false);

   // --- Combinar ref externo con interno ---
  const combinedRef = ref || inputRef;

  useEffect(() => setIsEditing(isEditable), [isEditable]);


   useEffect(() => {
        if (!matchName) return;
        const mc = matchCodes.find((m) => m.FICHA === matchName);
        if (!mc) return;
        setMatchCode(mc);
    }, [matchName]);
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickOutsideInput =
        !combinedRef.current || !combinedRef.current.contains(event.target);
      const clickOutsideMatchList =
        !matchListRef.current || !matchListRef.current.contains(event.target);

      if (clickOutsideInput && clickOutsideMatchList) {
        setShowMatchList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!visible) return null;
          
  const iconMatchCode= matchCode &&  matchCode.TIPO_PRESENTACIO==="1" ? 107: 9;

  const transformCase = (val) => {
    if (typeof val !== "string") return val;
    if (caseMode === "upper") return val.toUpperCase();
    if (caseMode === "lower") return val.toLowerCase();
    return val;
  };

  const borderClass = borderBottom ? "border-b-2" : "border-b";
  const wrapperClass =
    labelPosition === "left" ? "flex items-center mb-2" : "mb-2";
  const labelClass =
    labelPosition === "left"
      ? `${bold ? "font-bold" : "font-normal"} inline-block mr-2`
      : `${bold ? "font-bold" : "font-normal"} block mb-1`;

  const inputBaseClass = `
    flex-1 px-2 py-1
    ${disabled || !isEditing ? "input-disabled" : ""}
    ${highlight ? "input-highlight" : ""}
    appearance-none
  `;

  const renderLabel = (isRequired) => (
    <label
      htmlFor={name}
      className={`${labelClass} ${disabled ? "input-label-disabled" : ""}`}
    
    >
      {label}
      {isRequired && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const formatDouble = (val) => {
    if (val == null || val === "") return "";
    const num = Number(val);
    if (isNaN(num)) return "";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals ?? 2,
      maximumFractionDigits: decimals ?? 2,
    });
  };

  const parseDouble = (val) => {
    if (val == null || val === "") return "";
    const parsed = val.toString().replace(/\./g, "").replace(",", ".");
    const num = parseFloat(parsed);
    return isNaN(num) ? "" : num;
  };

  const handleKeyUp = (e) => {
    if (type === "password") {
      setCapsLock(e.getModifierState("CapsLock"));
    }
  };

  const handleChange = (val, onChangeRHF) => {
    if (type === "codigo" && val != null) {
      val = val.toString().toUpperCase(); // aquí ya transformamos
      if (length) val = val.slice(0, length);
    } else if (type === "double" && val != null) {
      val = parseDouble(val);
    } else if (caseMode === "upper" && val != null) {
      val = val.toUpperCase(); // <-- agregamos transformación general
    } else if (caseMode === "lower" && val != null) {
      val = val.toLowerCase();
    }

    if (!control) setLocalValue(val);
    if (onChangeRHF) onChangeRHF(val);
    onChangeProp?.(val);
  };
  const handleFocus = (e) => {
   /* if (type === "codigo" && length && /^\d+$/.test(e.target.value)) {
      e.target.value = parseInt(e.target.value, 10).toString();
    }*/
    if (type === "double") {
      e.target.value = localValue ?? "";
    }
  };

const handleBlur = (e, onChangeRHF) => {
  let val = e.target.value || "";

  if (type === "codigo" && length) {
    const padded = val.toString().padStart(length, "0");
    if (!control) setLocalValue(padded);
    if (onChangeRHF) onChangeRHF(padded);
    onChangeProp?.(padded);
    e.target.value = padded; // actualizar input con ceros
  }

  if (type === "double") {
    const formatted = formatDouble(val || 0); // mantiene decimales incluso si está vacío
    e.target.value = formatted;
    if (!control) setLocalValue(formatted);
    if (onChangeRHF) onChangeRHF(parseDouble(formatted));
    onChangeProp?.(parseDouble(formatted));
  }
};

  const handleMatchClick = async () => {
    if (!matchName) return;
    if (combinedRef.current) combinedRef.current.focus();
    setShowMatchList(!showMatchList);
  };

  const onSelectRow = (row, _fieldkey, value) => {
  setShowMatchList(false);

  if (onMatchClick) {
    // Si el padre define la función, la usamos
    onMatchClick(row, name, value, matchName);
  } else if (matchName) {
    // Si NO hay onMatchClick pero SÍ matchName, asignamos el valor seleccionado
    if (control) {
      // Para formularios controlados
      if (typeof _fieldkey === 'function') {
        _fieldkey(value);
      }
    } else {
      // Para modo no controlado
      if (combinedRef.current) {
        combinedRef.current.value = value;
      }
      setLocalValue(value);
    }
  }
};

  // --- Render del input con sufijo y match ---
  const renderInputWithMatch = (value, onChangeRHF, error) => {
    const isPassword = type === "password";

    const inputElement = (
      <input
        ref={combinedRef}
        id={name}
        type={
          type === "codigo" || type === "double"
            ? "text"
            : isPassword
            ? showPassword
              ? "text"
              : "password"
            : type
        }
        maxLength={length || undefined}
        value={value}
        onChange={(e) => handleChange(e.target.value, onChangeRHF)}
        onFocus={handleFocus}
        onBlur={(e) => handleBlur(e, onChangeRHF)}
        readOnly={readOnly}
        disabled={disabled}
        onKeyUp={handleKeyUp} // <-- Aquí añadimos la detección
        className={`${inputBaseClass} ${error ? "border-red-500" : ""} ${
          sufix ? "pr-10" : ""
        }`}
        placeholder={placeholder}
        style={width ? { width: `${width}ch` } : undefined}
      />
    );

    const renderSufix = sufix?.trim() !== "" && (
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-current text-sm">
        {sufix}
      </span>
    );

    if (isPassword) {
      return (
        <div className="relative flex-1"
            style={{ minWidth: labelMinWidth }}
            >
          {inputElement}
          {renderSufix}

          {/* Botón mostrar/ocultar */}
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
            <CTButton
              iconIndex={showPassword ? 103 : 102}
              variant="ghost"
              onClick={() => setShowPassword(!showPassword)}
              title={matchName}
              className="h-8 w-8 mt-1"
              focusRing={false}
            />
          </div>

          {/* Tooltip de Caps Lock */}
          {capsLock && (
            <div
              className="absolute left-1/2 -translate-x-1/2 -top-10 
                        bg-yellow-200 text-yellow-800 text-base 
                        px-2 py-1 rounded shadow-md z-20"
            >
              ⚠️ Mayúsculas activadas
            </div>
          )}
        </div>
      );
    }

    // Sin match
    if (!matchName && iconIndex < 0) {
      return (
        <div className="relative flex-1">
          {inputElement}
          {renderSufix}
        </div>
      );
    }

    
    // Con match "inside"
    if (matchPosition === "inside") {
      return (
        <div className="relative flex-1">
          {inputElement}
          {renderSufix}
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
            <CTButton
              iconIndex={iconMatchCode}
              variant="ghost"
              onClick={handleMatchClick}
              title={matchName}
              className="h-8 w-8 mt-1"
              focusRing={false}
            />
          </div>
          {showMatchList && matchName && (
            <div ref={matchListRef}>
              <CTMatchList
                matchName={matchName}
                onSelectRow={(row, fieldkey, value) =>
                  onSelectRow(row, fieldkey, value)
                }
              />
            </div>
          )}
        </div>
      );
    }

    // Con match "outside"
    return (
      <div className="flex w-full items-center gap-1">
        <div className="relative flex-1">
          {inputElement}
          {renderSufix}
        </div>
        <CTButton
          iconIndex={iconMatchCode}
          variant="ghost"
          onClick={handleMatchClick}
          title={matchName}
          className="w-10 h-10 flex items-center justify-center px-3"
          focusRing={false}
        />
        {showMatchList && matchName && (
          <div ref={matchListRef}>
            <CTMatchList
              matchName={matchName}
              onSelectRow={(row, rowid) => onSelectRow(row, rowid)}
            />
          </div>
        )}
      </div>
    );
  };

  // --- Render principal ---
  if (!control) {
    let displayValue = transformCase(propValue ?? localValue);
    if (type === "double") displayValue = formatDouble(displayValue);

    return (
      <div className={wrapperClass}>
        {renderLabel(required)}
        {!isEditing ? (
          <span
            className={`font-bold border border-gray-400 rounded-md px-2 py-1 w-full block h-10 ${
              highlight ? "input-highlight" : ""
            }`}
          >
            {displayValue}
            {sufix?.trim() !== "" && ` ${sufix}`}
          </span>
        ) : (
          <>{renderInputWithMatch(displayValue)}</>
        )}
      </div>
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{
        ...rules,
        ...(type === "number" &&
          decimals !== null && {
            validate: (val) => {
              const regex = new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`);
              return (
                val === "" || regex.test(val) || `Máx ${decimals} decimales`
              );
            },
          }),
        ...(type === "email" && {
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Email no válido",
          },
        }),
      }}
      render={({ field, fieldState }) => {
        const { value, onChange } = field;
        const { error } = fieldState;
        let displayValue = transformCase(value ?? "");
        if (type === "double") displayValue = formatDouble(displayValue);

        const isRequired = !!rules?.required;

        return (
          <div className={wrapperClass}>
            {renderLabel(isRequired)}
            {!isEditing ? (
              <span
                className={`font-bold border border-gray-400 rounded-md w-full block h-10 px-2 py-1 ${
                  highlight ? "input-highlight" : ""
                }`}
              >
                {displayValue}
                {sufix?.trim() !== "" && ` ${sufix}`}
              </span>
            ) : (
              <>{renderInputWithMatch(displayValue, onChange, error)}</>
            )}
            {error && (
              <p className="text-red-500 text-base mt-1">{error.message}</p>
            )}
          </div>
        );
      }}
    />
  );
}

CTEdit.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.any,
  type: PropTypes.oneOf([
    "text",
    "number",
    "email",
    "password",
    "date",
    "datetime-local",
    "time",
    "url",
    "double",
    "codigo",
  ]),
  length: PropTypes.number,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  visible: PropTypes.bool,
  labelPosition: PropTypes.oneOf(["top", "left"]),
  caseMode: PropTypes.oneOf(["normal", "upper", "lower"]),
  isEditable: PropTypes.bool,
  rules: PropTypes.object,
  required: PropTypes.bool,
  labelMinWidth: PropTypes.number,
  bold: PropTypes.bool,
  highlight: PropTypes.bool,
  decimals: PropTypes.number,
  showSpinner: PropTypes.bool,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  value: PropTypes.any,
  matchName: PropTypes.string,
  matchClick: PropTypes.func,
  matchPosition: PropTypes.oneOf(["inside", "outside"]),
  placeholder: PropTypes.string,
  borderBottom: PropTypes.bool,
  sufix: PropTypes.string, // sufijo opcional
};

export default CTEdit;
